import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Plus, Trash } from "lucide-react";
import { ulid } from "ulid";
import type { EsgBoxConfig } from "@/types/organization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { OrganizationFormValues } from "@/components/modals/organizationSettings/types";

interface Props {
  esgBoxes: EsgBoxConfig[];
  setEsgBoxes: React.Dispatch<React.SetStateAction<EsgBoxConfig[]>>;
  availableFolders: Array<{ id: string; name: string }>;
  onSubmit: (data: OrganizationFormValues) => Promise<void>;
  onCancel: () => void;
  isUpdating: boolean;
}

// ESG boxes are saved together with the general settings, so this tab submits the general form.
export const EsgPortalTab = ({ esgBoxes, setEsgBoxes, availableFolders, onSubmit, onCancel, isUpdating }: Props) => {
  const { handleSubmit } = useFormContext<OrganizationFormValues>();

  const updateEsgBox = (id: string, patch: Partial<EsgBoxConfig>) =>
    setEsgBoxes(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)));

  return (
    <div className="mt-6 space-y-6">
      <p className="text-sm text-gray-500">
        Configure the boxes that appear on the ESG Portal landing page. Each box links to Q&A scoped to the selected
        folders.
      </p>

      {esgBoxes.map((box, index) => (
        <div key={box.id} className="space-y-3 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <Label>Box {index + 1}</Label>
            <button
              type="button"
              onClick={() => setEsgBoxes(prev => prev.filter(b => b.id !== box.id))}
              className="text-gray-400 hover:text-red-500"
            >
              <Trash size={16} />
            </button>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-normal text-gray-500">Label</Label>
            <Input
              value={box.label}
              onChange={e => updateEsgBox(box.id, { label: e.target.value })}
              placeholder="e.g. Smiths Group"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-normal text-gray-500">Sector</Label>
            <Input
              value={box.sector ?? ""}
              onChange={e => updateEsgBox(box.id, { sector: e.target.value || undefined })}
              placeholder="e.g. Industrial Technology - UK"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-normal text-gray-500">Description</Label>
            <textarea
              value={box.description ?? ""}
              onChange={e => updateEsgBox(box.id, { description: e.target.value || undefined })}
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Brief description of the company/division"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-normal text-gray-500">Folder</Label>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
              {availableFolders.map(folder => (
                <label key={folder.id} className="flex cursor-pointer items-center gap-2 rounded p-1 hover:bg-gray-50">
                  <input
                    type="radio"
                    name={`esg-folder-${box.id}`}
                    checked={box.folderId === folder.id}
                    onChange={() => updateEsgBox(box.id, { folderId: folder.id })}
                    className="h-4 w-4 border-gray-300"
                  />
                  <span className="text-sm">{folder.name}</span>
                </label>
              ))}
              {availableFolders.length === 0 && (
                <p className="py-2 text-center text-xs text-gray-400">No shared folders found</p>
              )}
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={() => setEsgBoxes(prev => [...prev, { id: ulid(), label: "", folderId: "" }])}
        className="w-full"
      >
        <Plus size={16} className="mr-2" />
        Add Box
      </Button>

      <div className="flex justify-end gap-3 border-t pt-6">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isUpdating}>
          Cancel
        </Button>
        <Button type="button" disabled={isUpdating} onClick={handleSubmit(onSubmit)}>
          {isUpdating ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};
